import React, { useEffect, useState } from 'react'
import { PageHeader } from '@/components/layout'
import { useUsers, useDeleteUser, useUpdateUser, useResetUserTOTP } from '@/hooks/useUsers'
import { AffiliateCourseCommissionPanel } from '@/components/affiliates'
import { affiliateService } from '@/services/affiliateService'
import { CreateStudentModal } from '@/components/admin/CreateStudentModal'
import type { User } from '@/services/userService'
import type { AffiliateCourseCommission } from '@/types/affiliate'

/**
 * AdminUsersPage manages Peserta (students) only. Teachers, admins, and
 * super admins each have dedicated management surfaces — this page
 * intentionally does NOT expose role changes or allow creating staff
 * accounts. See AdminInstructorsPage and AdminStaffPage.
 */
export const AdminUsersPage: React.FC = () => {
    const [searchQuery, setSearchQuery] = useState('')
    const [deletingUser, setDeletingUser] = useState<User | null>(null)
    const [togglingUser, setTogglingUser] = useState<User | null>(null)
    const [resettingTOTPUser, setResettingTOTPUser] = useState<User | null>(null)
    const [showCreateModal, setShowCreateModal] = useState(false)

    // Commission management state — some students also participate in the
    // affiliate programme; this modal shows course-level commission overrides.
    const [managingCommissionsFor, setManagingCommissionsFor] = useState<User | null>(null)
    const [currentAffiliateId, setCurrentAffiliateId] = useState<string | null>(null)
    const [affiliateCommissions, setAffiliateCommissions] = useState<AffiliateCourseCommission[]>([])
    const [loadingCommissions, setLoadingCommissions] = useState(false)

    const { data: users, isLoading, error } = useUsers()
    const deleteMutation = useDeleteUser()
    const updateMutation = useUpdateUser()
    const resetTOTPMutation = useResetUserTOTP()

    useEffect(() => {
        if (managingCommissionsFor) {
            fetchAffiliateCommissions(managingCommissionsFor.id)
        }
    }, [managingCommissionsFor])

    const fetchAffiliateCommissions = async (userId: string) => {
        setLoadingCommissions(true)
        try {
            const affiliate = await affiliateService.getAffiliateByUserID(userId)
            setCurrentAffiliateId(affiliate.id)
            const commissions = await affiliateService.getAffiliateCourseCommissions(affiliate.id)
            setAffiliateCommissions(commissions)
        } catch {
            setCurrentAffiliateId(null)
            setAffiliateCommissions([])
        } finally {
            setLoadingCommissions(false)
        }
    }

    const closeCommissionModal = () => {
        setManagingCommissionsFor(null)
        setCurrentAffiliateId(null)
        setAffiliateCommissions([])
    }

    // Scope to students only. Teachers/admins/super_admins are managed
    // on their own pages. Affiliates are a flag on top of the student
    // tier — they also show up here.
    const studentsOnly = (users || []).filter(
        (u) => u.role === 'student' || u.role === 'affiliate',
    )

    const filteredUsers = studentsOnly.filter((user) => {
        const q = searchQuery.toLowerCase()
        return (
            user.name.toLowerCase().includes(q) ||
            user.email.toLowerCase().includes(q)
        )
    })

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <p className="text-red-500">Gagal memuat peserta</p>
            </div>
        )
    }

    return (
        <div data-testid="admin-users-page">
            <PageHeader
                title="Kelola Peserta"
                subtitle="Tambah dan kelola peserta platform"
                actions={
                    <button
                        type="button"
                        onClick={() => setShowCreateModal(true)}
                        className="px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-bold hover:scale-105 transition-transform"
                    >
                        Tambah Peserta
                    </button>
                }
            />

            {searchQuery && (
                <div className="mb-4 p-3 rounded bg-blue-500/10 text-blue-300 text-sm">
                    Menampilkan hasil pencarian untuk: "{searchQuery}"
                    <button onClick={() => setSearchQuery('')} className="ml-2 underline">Reset</button>
                </div>
            )}

            <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
                <div className="relative flex-1 min-w-0 md:max-w-md">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-500 text-[20px]">
                        search
                    </span>
                    <input
                        type="text"
                        placeholder="Cari peserta..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary/50"
                    />
                </div>
            </div>

            <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[850px]">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-white/5">
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Nama</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Email</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden md:table-cell">Telepon</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden md:table-cell">Status</th>
                                <th className="text-left px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted hidden lg:table-cell">Dibuat</th>
                                <th className="text-right px-5 py-4 text-xs font-bold uppercase text-gray-500 dark:text-text-muted">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-white/5">
                            {isLoading && (
                                <tr>
                                    <td colSpan={6} className="px-5 py-8 text-center text-gray-500 dark:text-text-muted">
                                        Memuat...
                                    </td>
                                </tr>
                            )}
                            {!isLoading && filteredUsers.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-5 py-8 text-center text-gray-500 dark:text-text-muted">
                                        Belum ada peserta.
                                    </td>
                                </tr>
                            )}
                            {filteredUsers.map((user) => (
                                <tr
                                    key={user.id}
                                    className="hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                                    data-testid={`user-row-${user.id}`}
                                >
                                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white">{user.name}</td>
                                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted">{user.email}</td>
                                    <td className="px-5 py-4 text-sm text-gray-500 dark:text-text-muted hidden md:table-cell">{user.phone || '-'}</td>
                                    <td className="px-5 py-4 hidden md:table-cell">
                                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${
                                            user.isActive
                                                ? 'bg-emerald-500 text-white'
                                                : 'bg-slate-400 text-white'
                                        }`}>
                                            {user.isActive ? 'Aktif' : 'Nonaktif'}
                                        </span>
                                    </td>
                                    <td className="px-5 py-4 text-xs text-gray-500 dark:text-text-muted hidden lg:table-cell">
                                        {new Date(user.createdAt).toLocaleDateString('id-ID')}
                                    </td>
                                    <td className="px-5 py-4">
                                        <div className="flex items-center justify-end gap-2">
                                            {user.role === 'affiliate' && (
                                                <button
                                                    onClick={() => setManagingCommissionsFor(user)}
                                                    className="size-10 md:size-8 rounded-lg bg-purple-500 text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                                                    title="Kelola Komisi"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">payments</span>
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setTogglingUser(user)}
                                                className={`size-10 md:size-8 rounded-lg flex items-center justify-center text-white transition-opacity ${
                                                    user.isActive
                                                        ? 'bg-amber-500 hover:opacity-90'
                                                        : 'bg-emerald-500 hover:opacity-90'
                                                }`}
                                                title={user.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                                            >
                                                <span className="material-symbols-outlined text-[16px]">
                                                    {user.isActive ? 'block' : 'check_circle'}
                                                </span>
                                            </button>
                                            <button
                                                onClick={() => setDeletingUser(user)}
                                                className="size-10 md:size-8 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center hover:opacity-90 transition-opacity"
                                                title="Hapus"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">delete</span>
                                            </button>
                                            <button
                                                onClick={() => setResettingTOTPUser(user)}
                                                className="size-10 md:size-8 rounded-lg bg-purple-500 text-white flex items-center justify-center hover:opacity-90 transition-opacity"
                                                title="Reset TOTP"
                                            >
                                                <span className="material-symbols-outlined text-[16px]">phonelink_lock</span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {togglingUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setTogglingUser(null)} />
                    <div className="relative bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            {togglingUser.isActive ? 'Nonaktifkan Peserta' : 'Aktifkan Peserta'}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-6">
                            {togglingUser.isActive ? (
                                <>Nonaktifkan <span className="font-medium text-gray-900 dark:text-white">{togglingUser.name}</span>? Peserta tidak akan bisa login sampai diaktifkan kembali.</>
                            ) : (
                                <>Aktifkan <span className="font-medium text-gray-900 dark:text-white">{togglingUser.name}</span>? Peserta akan bisa login kembali.</>
                            )}
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setTogglingUser(null)}
                                className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                            >
                                Batal
                            </button>
                            <button
                                onClick={() => {
                                    updateMutation.mutate({
                                        id: togglingUser.id,
                                        data: { isActive: !togglingUser.isActive },
                                    })
                                    setTogglingUser(null)
                                }}
                                disabled={updateMutation.isPending}
                                className={`px-4 py-2 text-white font-bold rounded-lg transition-opacity disabled:opacity-50 ${
                                    togglingUser.isActive
                                        ? 'bg-amber-500 hover:opacity-90'
                                        : 'bg-emerald-500 hover:opacity-90'
                                }`}
                            >
                                {updateMutation.isPending ? 'Memproses...' : (togglingUser.isActive ? 'Nonaktifkan' : 'Aktifkan')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deletingUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setDeletingUser(null)} />
                    <div className="relative bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Hapus Peserta</h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-6">
                            Hapus <span className="font-medium text-gray-900 dark:text-white">{deletingUser.name}</span> secara permanent? Aksi ini tidak dapat dibatalkan.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeletingUser(null)}
                                className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                            >
                                Batal
                            </button>
                            <button
                                onClick={() => {
                                    deleteMutation.mutate(deletingUser.id)
                                    setDeletingUser(null)
                                }}
                                disabled={deleteMutation.isPending}
                                className="px-4 py-2 bg-rose-500 hover:opacity-90 text-white font-bold rounded-lg transition-opacity disabled:opacity-50"
                            >
                                {deleteMutation.isPending ? 'Memproses...' : 'Hapus Permanent'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {resettingTOTPUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setResettingTOTPUser(null)} />
                    <div className="relative bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Reset TOTP</h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-6">
                            Reset TOTP untuk <span className="font-medium text-gray-900 dark:text-white">{resettingTOTPUser.name}</span>? Peserta harus scan ulang QR code Google Authenticator saat login berikutnya.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setResettingTOTPUser(null)}
                                className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 transition-colors"
                            >
                                Batal
                            </button>
                            <button
                                onClick={() => {
                                    resetTOTPMutation.mutate(resettingTOTPUser.id)
                                    setResettingTOTPUser(null)
                                }}
                                disabled={resetTOTPMutation.isPending}
                                className="px-4 py-2 bg-purple-500 hover:opacity-90 text-white font-bold rounded-lg transition-opacity disabled:opacity-50"
                            >
                                {resetTOTPMutation.isPending ? 'Memproses...' : 'Reset TOTP'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {managingCommissionsFor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={closeCommissionModal} />
                    <div className="relative bg-white dark:bg-surface-card border border-slate-200 dark:border-white/10 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    Kelola Komisi: {managingCommissionsFor.name}
                                </h2>
                                <p className="text-sm text-gray-500 dark:text-text-muted">
                                    Atur tarif komisi khusus untuk kursus tertentu
                                </p>
                            </div>
                            <button
                                onClick={closeCommissionModal}
                                className="size-8 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                            >
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6">
                            {loadingCommissions ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                </div>
                            ) : currentAffiliateId ? (
                                <AffiliateCourseCommissionPanel
                                    mode="by-affiliate"
                                    affiliateId={currentAffiliateId}
                                    commissions={affiliateCommissions}
                                    onRefresh={() => fetchAffiliateCommissions(managingCommissionsFor.id)}
                                />
                            ) : (
                                <div className="text-center py-8 text-gray-500 dark:text-text-muted">
                                    Gagal memuat data affiliate
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <CreateStudentModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
            />
        </div>
    )
}

export default AdminUsersPage
